/* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any, no-unused-vars, no-nested-ternary */
import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { Dices } from 'lucide-react';
import { ResponseField, PromptTestResponse } from '../types';
import { VoiceProfileSelector } from '../../../shared/components/VoiceProfileSelector';
import { supabase } from '../../../services/supabase';

/**
 * Convert path or URL to direct storage URL with cache busting
 * This ensures fresh images are always loaded in the debugger
 *
 * Handles multiple formats:
 * - Storage path: "temp/debug-console-image.png"
 * - Optimization URL: "{BASE_URL}/image/media/temp/debug-console-image.png?size=300"
 * - Supabase URL: "{SUPABASE_URL}/storage/v1/object/public/media/temp/debug-console-image.png"
 * - External URL: "https://external.com/image.png"
 */
const getDirectStorageUrl = (pathOrUrl: string): string => {
  if (!pathOrUrl) return pathOrUrl;

  try {
    // Check if it's a simple storage path (no protocol)
    // Format: "temp/debug-console-image.png" or "shows/my-show/123.png"
    if (!pathOrUrl.includes('://') && !pathOrUrl.startsWith('/')) {
      // Direct path - use it as-is
      const { data } = supabase.storage.from('media').getPublicUrl(pathOrUrl);
      return `${data.publicUrl}?t=${Date.now()}`;
    }

    // Check if this is an optimization URL (contains /image/ endpoint)
    // Format: {BASE_URL}/image/media/temp/debug-console-image.png?size=300&format=webp
    const optimizationPattern = /\/image\/(.+?)(?:\?|$)/;
    const match = pathOrUrl.match(optimizationPattern);

    if (match) {
      // Extract the storage path (e.g., "media/temp/debug-console-image.png")
      let storagePath = match[1];

      // Remove 'media/' prefix if present since supabase.storage.from('media') already specifies the bucket
      if (storagePath.startsWith('media/')) {
        storagePath = storagePath.substring(6);
      }

      // Get direct public URL from Supabase Storage
      const { data } = supabase.storage.from('media').getPublicUrl(storagePath);

      // Add cache-busting timestamp
      return `${data.publicUrl}?t=${Date.now()}`;
    }

    // If not an optimization URL, check if it's a Supabase storage URL
    // Format: {SUPABASE_URL}/storage/v1/object/public/media/temp/debug-console-image.png
    const storagePattern = /\/storage\/v1\/object\/public\/media\/(.+)$/;
    const storageMatch = pathOrUrl.match(storagePattern);

    if (storageMatch) {
      const storagePath = storageMatch[1];
      const { data } = supabase.storage.from('media').getPublicUrl(storagePath);
      return `${data.publicUrl}?t=${Date.now()}`;
    }

    // For external URLs (e.g., OpenAI), just add cache busting
    const separator = pathOrUrl.includes('?') ? '&' : '?';
    return `${pathOrUrl}${separator}t=${Date.now()}`;
  } catch (error) {
    console.warn('Failed to convert to direct storage URL:', error);
    // Fallback to original URL with cache busting
    const separator = pathOrUrl.includes('?') ? '&' : '?';
    return `${pathOrUrl}${separator}t=${Date.now()}`;
  }
};

interface ResponseDisplayProps {
  fields: ResponseField[];
  response?: PromptTestResponse;
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
  dropdownOptions?: Record<string, Array<{ value: string; label: string }>>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ height: '100%' }}>
    {value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
  </div>
);

export const ResponseDisplay: React.FC<ResponseDisplayProps> = ({
  fields,
  response,
  data,
  onChange,
  dropdownOptions = {},
}) => {
  const [tabValue, setTabValue] = useState(0);
  const notify = useNotify();

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFieldChange = (key: string, value: string) => {
    // Only update if the value actually changed to prevent unnecessary re-renders
    if (data[key] !== value) {
      onChange({ ...data, [key]: value });
    }
  };

  const getFieldValue = (key: string): string => {
    const raw = data[key];
    if (raw === null || raw === undefined) return '';
    // #766: object-valued fields (e.g. delivery_spec) render as pretty JSON
    // rather than "[object Object]".
    if (typeof raw === 'object') return JSON.stringify(raw, null, 2);
    return String(raw);
  };

  const getWordCount = (text: string): number => {
    return text
      .trim()
      .split(/\s+/)
      .filter(word => word.length > 0).length;
  };

  const getCharCount = (text: string): number => {
    return text.length;
  };

  const handleCopyParam = (paramName: string) => {
    const textToCopy = `{${paramName}}`;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        notify(`Copied: ${textToCopy}`, { type: 'success' });
      })
      .catch(() => {
        notify('Failed to copy to clipboard', { type: 'error' });
      });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minWidth: 0,
      }}
    >
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Fields" />
          <Tab label="JSON" />
        </Tabs>
      </Box>

      {/* Fields Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box
          sx={{
            padding: 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            minWidth: 0, // Allow content to shrink
          }}
        >
          {fields.map(field => {
            const value = getFieldValue(field.key);
            const wordCount = getWordCount(value);
            const charCount = getCharCount(value);

            // Skip dropdown fields that have no options (hide them completely)
            if (
              field.type === 'dropdown' &&
              (!dropdownOptions?.[field.key] ||
                dropdownOptions[field.key]?.length === 0)
            ) {
              return null;
            }

            return (
              <Box key={field.key} sx={{ minWidth: 0 }}>
                {field.type === 'dropdown' ? (
                  <Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}
                    >
                      <FormControl fullWidth sx={{ minWidth: 0 }}>
                        <InputLabel id={`${field.key}-label`}>
                          {field.label}
                        </InputLabel>
                        <Select
                          labelId={`${field.key}-label`}
                          value={value || ''}
                          label={field.label}
                          onChange={e =>
                            handleFieldChange(field.key, e.target.value)
                          }
                        >
                          {dropdownOptions[field.key]?.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {field.key === 'selectedShow' &&
                        (dropdownOptions[field.key]?.length || 0) > 1 && (
                          <Tooltip title="Random selection">
                            <IconButton
                              onClick={() => {
                                // Get random show (exclude the first item which is "Populate from a show")
                                const showOptions =
                                  dropdownOptions[field.key]?.slice(1) || [];
                                if (showOptions.length > 0) {
                                  const randomIndex = Math.floor(
                                    Math.random() * showOptions.length
                                  );
                                  const randomShow = showOptions[randomIndex];
                                  if (randomShow?.value) {
                                    handleFieldChange(
                                      field.key,
                                      randomShow.value
                                    );
                                  }
                                }
                              }}
                              size="large"
                              sx={{ alignSelf: 'flex-end' }}
                            >
                              <Dices />
                            </IconButton>
                          </Tooltip>
                        )}

                      {field.key === 'selectedStory' &&
                        (dropdownOptions[field.key]?.length || 0) > 1 && (
                          <Tooltip title="Random selection">
                            <IconButton
                              onClick={() => {
                                // Get random story (exclude the first item which is "Populate from a story")
                                const storyOptions =
                                  dropdownOptions[field.key]?.slice(1) || [];
                                if (storyOptions.length > 0) {
                                  const randomIndex = Math.floor(
                                    Math.random() * storyOptions.length
                                  );
                                  const randomStory = storyOptions[randomIndex];
                                  if (randomStory?.value) {
                                    handleFieldChange(
                                      field.key,
                                      randomStory.value
                                    );
                                  }
                                }
                              }}
                              size="large"
                              sx={{ alignSelf: 'flex-end' }}
                            >
                              <Dices />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Box>
                  </Box>
                ) : field.type === 'image' ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {field.label}
                    </Typography>
                    {value ? (
                      <Box
                        component="img"
                        src={getDirectStorageUrl(value as string)}
                        alt={field.label}
                        sx={{
                          maxWidth: 300,
                          height: 'auto',
                          border: 1,
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8,
                          },
                        }}
                        onClick={() => {
                          // Open direct storage URL without optimization
                          const directUrl = getDirectStorageUrl(
                            value as string
                          );
                          window.open(directUrl, '_blank');
                        }}
                        title="Click to open full size in new tab"
                      />
                    ) : (
                      <Paper
                        variant="outlined"
                        sx={{
                          padding: 4,
                          textAlign: 'center',
                          backgroundColor: 'grey.50',
                          minHeight: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No image generated yet. Run the prompt to generate an
                          image.
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                ) : field.key === 'voiceProfile1' ||
                  field.key === 'voiceProfile2' ? (
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <VoiceProfileSelector
                        value={value}
                        onChange={profileKey =>
                          handleFieldChange(field.key, profileKey)
                        }
                        label={field.label}
                      />
                    </Box>
                    <TextField
                      label={`Voice ${field.key === 'voiceProfile1' ? '1' : '2'} Index`}
                      type="number"
                      value={
                        getFieldValue(
                          field.key === 'voiceProfile1'
                            ? 'voiceIndex1'
                            : 'voiceIndex2'
                        ) || ''
                      }
                      onChange={e =>
                        handleFieldChange(
                          field.key === 'voiceProfile1'
                            ? 'voiceIndex1'
                            : 'voiceIndex2',
                          e.target.value
                        )
                      }
                      variant="outlined"
                      size="small"
                      sx={{ width: '80px' }}
                      inputProps={{
                        min: 0,
                        max: 99,
                      }}
                    />
                  </Box>
                ) : field.key === 'voiceIndex1' ||
                  field.key ===
                    'voiceIndex2' ? // Skip rendering voice index fields - they're rendered with voice profiles
                null : (
                  <TextField
                    fullWidth
                    label={field.label}
                    multiline={field.type === 'textarea'}
                    rows={
                      field.type === 'textarea'
                        ? (field.rows ??
                          (field.key === 'script'
                            ? 8
                            : field.key === 'musicPrompt' ||
                                field.key === 'imagePrompt'
                              ? 2
                              : 4))
                        : 1
                    }
                    value={value}
                    onChange={e => handleFieldChange(field.key, e.target.value)}
                    variant="outlined"
                    sx={{
                      minWidth: 0,
                      '& .MuiInputBase-inputMultiline': {
                        resize: field.type === 'textarea' ? 'vertical' : 'none',
                      },
                    }}
                  />
                )}

                {/* Character and word count - only show for text fields */}
                {field.type !== 'dropdown' &&
                  field.type !== 'image' &&
                  field.key !== 'voiceProfile1' &&
                  field.key !== 'voiceProfile2' &&
                  field.key !== 'voiceIndex1' &&
                  field.key !== 'voiceIndex2' && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {charCount} chr {wordCount} wrd
                      </Typography>

                      {/* Parameter indicator - clickable to copy */}
                      {field.isParam && field.paramName && (
                        <Tooltip title="Click to copy parameter name" arrow>
                          <Typography
                            variant="caption"
                            color="primary.main"
                            onClick={() => handleCopyParam(field.paramName!)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.dark',
                              },
                            }}
                          >
                            {`{${field.paramName}}`}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  )}
              </Box>
            );
          })}
        </Box>
      </TabPanel>

      {/* JSON Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ padding: 2, height: '100%', minWidth: 0 }}>
          <Paper
            variant="outlined"
            sx={{
              padding: 2,
              height: '100%',
              overflow: 'auto',
              backgroundColor: 'grey.50',
              minWidth: 0,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Response Content:
            </Typography>

            {(() => {
              // Use response if available, otherwise use rawResponse from data (queue-based flow)
              const jsonData = response || (data.rawResponse as any);

              if (jsonData) {
                return (
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      overflowWrap: 'break-word',
                      maxWidth: '100%',
                      overflow: 'auto',
                    }}
                  >
                    {(() => {
                      try {
                        // Handle response object from old synchronous flow
                        if (response?.result) {
                          if (typeof response.result === 'string') {
                            const parsed = JSON.parse(response.result);
                            return JSON.stringify(parsed, null, 2);
                          }
                          return JSON.stringify(response.result, null, 2);
                        }
                        // Handle rawResponse from queue-based flow
                        if (typeof jsonData === 'string') {
                          const parsed = JSON.parse(jsonData);
                          return JSON.stringify(parsed, null, 2);
                        }
                        return JSON.stringify(jsonData, null, 2);
                      } catch (e) {
                        // If parsing fails, show the raw data
                        return typeof jsonData === 'string'
                          ? jsonData
                          : JSON.stringify(jsonData, null, 2);
                      }
                    })()}
                  </pre>
                );
              }

              return (
                <Typography variant="body2" color="text.secondary">
                  No response data available. Run the prompt to see results.
                </Typography>
              );
            })()}
          </Paper>
        </Box>
      </TabPanel>
    </Box>
  );
};
